import { useEffect, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  apiClient,
  clearStepUpCooldown,
  currentSpaPath,
  setStepUpUnsatisfiedHandler,
  takePendingStepUp,
  STEP_UP_REQUIRED_CODE,
} from '@/shared/api';
import type { NormalizedApiError, PendingStepUpAction } from '@/shared/api';
import { publishGlobalFeedback } from '@/shared/feedback';

/**
 * The second half of the step-up round-trip (plan-auth-001 Phase 4.4, adr-auth-004).
 *
 * `apiClient`'s response interceptor owns the first half: a `403 STEP_UP_REQUIRED` parks the interrupted
 * action and navigates to Keycloak with `acr_values`. This component owns what happens on the way back in —
 * **it replays the action the user started, so they never have to remember what they were doing.** Without
 * it a step-up would silently swallow every high-risk change: the user re-authenticates, lands on their
 * screen, and the refund they issued (or the credential they rotated) simply never happened.
 *
 * It renders nothing. Mount it once, inside the authenticated shell (`AppLayout`) and **after**
 * `GlobalFeedbackSnackbar` — the feedback bus keeps exactly one listener, registered in that component's
 * effect, and effects run in tree order, so a message published from this component's own effect would be
 * dropped if it mounted first.
 *
 * ## Why the copy always explains itself
 *
 * A step-up is a challenge, not a refusal, and the user experienced it as "I pressed Save and was thrown at
 * a login screen". Every outcome below therefore says *what happened and why* rather than reporting a bare
 * success or failure: the sign-in was asked for because the action is high-risk, and here is where that
 * left the action.
 */
export function StepUpReplay() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  // A replay must happen at most once per document. The ref survives StrictMode's double-invoked effects,
  // which would otherwise fire the parked mutation twice.
  const replayStarted = useRef(false);

  // A challenge that arrives while the previous one is still in its cooldown window means the Keycloak
  // round-trip did not raise the assurance level. The interceptor refuses to redirect again (that is the
  // loop it exists to break) and reports it here so the dead end is explained instead of silent.
  useEffect(() => {
    setStepUpUnsatisfiedHandler(() => {
      publishGlobalFeedback({ severity: 'warning', message: t('stepUp.notRaised') });
    });
    return () => setStepUpUnsatisfiedHandler(null);
  }, [t]);

  useEffect(() => {
    if (replayStarted.current) {
      return;
    }
    // Consume-once, and only on the screen the action came from — see `takePendingStepUp`.
    const pending = takePendingStepUp(currentSpaPath());
    if (!pending) {
      return;
    }
    replayStarted.current = true;

    if (!pending.replayable) {
      // A `FormData` upload: the file cannot be carried across a full-page navigation, so there is nothing
      // faithful to re-send. The user is at least back on the right screen and told exactly what to redo —
      // far better than a change that appears to have been made and was not.
      publishGlobalFeedback({ severity: 'info', message: t('stepUp.repeatAction') });
      return;
    }

    const replay = async (action: PendingStepUpAction): Promise<void> => {
      try {
        await apiClient.request({
          method: action.method,
          url: action.url,
          baseURL: action.baseURL,
          params: action.params,
          data: action.data,
        });
        // The request went through, which is proof the round-trip DID raise the assurance level. The
        // loop the cooldown guards against cannot be happening, so stop suppressing the next challenge.
        clearStepUpCooldown();
        // The response goes nowhere — the mutation that would have consumed it died with the previous
        // document — so the cache is the only thing that can carry the result to the UI. Invalidate
        // everything: the replayed call changed server state this SPA has no map of, and the session
        // itself may now resolve different permissions.
        await queryClient.invalidateQueries();
        publishGlobalFeedback({ severity: 'success', message: t('stepUp.replayed') });
      } catch (error) {
        const normalized = error as NormalizedApiError;
        if (normalized?.code === STEP_UP_REQUIRED_CODE) {
          // Challenged again straight after re-authenticating: the interceptor's cooldown branch has
          // already published the explanation, so saying it twice would just replace it with itself.
          return;
        }
        publishGlobalFeedback({
          severity: 'error',
          message: t('stepUp.replayFailed', {
            message: normalized?.message ?? t('error.unknown'),
          }),
        });
      }
    };

    void replay(pending);
  }, [queryClient, t]);

  return null;
}
